import { createContext, useContext, useState, useCallback, useMemo } from 'react'

const STORAGE_KEY = 'solcheddar_trade_defaults'

const DEFAULT_TRADE_DEFAULTS = {
  buyAmountSol: 0.1,       // SOL to spend on market buys
  sellPercentage: 100,     // % of balance to sell on market sells
  slippageBps: 1000,       // 10% slippage (in basis points)
  priorityFeeLamports: 100000, // 0.0001 SOL priority fee
  jitoTipLamports: 0,      // Jito tip (0 = disabled)
  orderVenue: 'jupiter',   // Solana market + limit venue: 'jupiter' | 'meteora'
  // ─── Hyperliquid futures ───
  futuresQty: 0.01,        // Base coin qty for market orders
  futuresLeverage: 10,     // Default leverage
}

function loadDefaults() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_TRADE_DEFAULTS }
    return { ...DEFAULT_TRADE_DEFAULTS, ...JSON.parse(raw) }
  } catch {
    return { ...DEFAULT_TRADE_DEFAULTS }
  }
}

const TradeDefaultsContext = createContext(null)

export function TradeDefaultsProvider({ children }) {
  const [defaults, setDefaultsState] = useState(loadDefaults)

  const setDefaults = useCallback((updates) => {
    setDefaultsState(prev => {
      const next = { ...prev, ...updates }
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch {}
      return next
    })
  }, [])

  const resetDefaults = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY) } catch {}
    setDefaultsState({ ...DEFAULT_TRADE_DEFAULTS })
  }, [])

  // Memoized: an inline object literal here gave every consumer a NEW context
  // value on every provider render, so React re-rendered all of them even when
  // `defaults` had not changed. FootprintChart is a consumer, and each of its
  // renders costs ~25ms — context updates bypass React.memo entirely, so this
  // is not optional once the chart is memoized. Both setters are useCallback'd,
  // leaving `defaults` as the only real dependency.
  const value = useMemo(
    () => ({ defaults, setDefaults, resetDefaults }),
    [defaults, setDefaults, resetDefaults]
  )

  return (
    <TradeDefaultsContext.Provider value={value}>
      {children}
    </TradeDefaultsContext.Provider>
  )
}

export function useTradeDefaults() {
  const ctx = useContext(TradeDefaultsContext)
  if (!ctx) throw new Error('useTradeDefaults must be used inside TradeDefaultsProvider')
  return ctx
}

export { DEFAULT_TRADE_DEFAULTS }
